import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Svg, Typography } from '@altermaind/web/ui';
import { Routes } from '@/_shared/constants';
import { useDialog, useNotification } from '@/_shared/hooks';
import { getOrganizationsCall, putSetOrganizationCall } from '@/core/state/api';
import { Badge } from '../Badge';

export const DialogSwitchOrganization: FC = () => {
  const t = useTranslations('errorBoundary.dialogSwitchOrganization');
  const orgT = useTranslations('general.switchOrganization');
  const sharedT = useTranslations();
  const { notify } = useNotification();
  const { Dialog, closeDialog, isOpen } = useDialog('switchOrganizationDialog');
  const [onMouseHover, setOnMouseHover] = useState<string | undefined>(undefined);
  const [selectedOrg, setSelectedOrg] = useState<{ orgaId: string; userId: string } | undefined>(
    undefined,
  );
  const {
    execute: executeGetOrganizations,
    defaultOrganizationId,
    data,
    success: successGetOrganizations,
    error: errorGetOrganizations,
    loading: loadingGetOrganizations,
  } = getOrganizationsCall.hook();
  const { execute: executePutSetOrganization, loading: loadingPutSetOrganization } =
    putSetOrganizationCall.hook();

  const onConfirm = useCallback(() => {
    if (selectedOrg && selectedOrg.orgaId !== defaultOrganizationId) {
      executePutSetOrganization({
        organization: selectedOrg.orgaId,
        userId: selectedOrg.userId,
        successSaga: () => {
          notify(sharedT('success.genericApiSuccess'), 'success');
          setTimeout(() => {
            window.location.href = Routes.Homepage;
          }, 1000);
        },
        errorSaga: () => {
          notify(sharedT('errors.genericApiError'), 'error');
        },
      });
    }
  }, [selectedOrg, executePutSetOrganization, notify, sharedT, defaultOrganizationId]);

  useEffect(() => {
    if (!successGetOrganizations && !loadingGetOrganizations && !errorGetOrganizations) {
      executeGetOrganizations({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog open={isOpen} onClose={closeDialog} closeButton title={undefined} maxWidth='700px'>
      <Box width='100%' display='flex' flexDirection='column' alignItems='center' px='s' pb='s'>
        <Typography variant='h3' color='primary/text' textAlign='center'>
          {t('title')}
        </Typography>
        <Typography variant='p2/light' color='primary/text' textAlign='center' mt='xxs'>
          {t('description')}
        </Typography>

        <Box display='flex' flexDirection='column' width='100%' mt='m'>
          {data.map((item, index) => (
            <Box key={item.organizationId} width='100%'>
              <Box
                p='xxs'
                display='flex'
                alignItems='center'
                alignContent='center'
                borderRadius='l'
                style={{ cursor: 'pointer' }}
                flex={1}
                width='100%'
                onClick={() =>
                  setSelectedOrg(
                    item.organizationId !== defaultOrganizationId
                      ? { orgaId: item.organizationId, userId: item.userId }
                      : undefined,
                  )
                }
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...(onMouseHover === item.organizationId ||
                selectedOrg?.orgaId === item.organizationId
                  ? { backgroundColor: 'grey/dark' }
                  : {})}
                onMouseEnter={() => setOnMouseHover(item.organizationId)}
                onMouseLeave={() => setOnMouseHover(undefined)}
              >
                <Svg name='company' size='xs' />
                <Typography ml='xxs' variant='p2/light' fontSize='xs' flex={1}>
                  {item.name}
                </Typography>
                {item.organizationId === defaultOrganizationId && (
                  <Badge
                    label={orgT('nowLogged')}
                    textProps={{ fontSize: 'xxxs', color: 'primary/text' }}
                    ml='xs'
                    py='none'
                    px='xxs'
                    opacity={0.9}
                  />
                )}
              </Box>
              {index < data.length - 1 && (
                <Box height={1} backgroundColor='grey/light/background' width='100%' my='xxs' />
              )}
            </Box>
          ))}
        </Box>

        <Box display='flex' flexDirection='row' justifyContent='center' mt='l' style={{ gap: 16 }}>
          <Button variant='link/primary' onClick={closeDialog} style={{ textDecoration: 'none' }}>
            <Svg name='close' fill='primary/text' size='s' mr='xs' />
            {t('cancel')}
          </Button>
          <Button
            spinnerColor='white'
            loading={loadingPutSetOrganization}
            variant='primary'
            onClick={onConfirm}
            disabled={!selectedOrg}
          >
            {t('confirm')}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};
